import { routeFactory } from "@/RouteFactory";
import { useCurrencySelection } from "@/redux/profile/hooks/UseCurrencySelection";
import {
  StatisticsContext,
  StatisticsContextType,
} from "@/features/statistics/StatisticsContext";
import { createResultTableConfig } from "@/features/statistics/config/ResultTableConfig";
import { useStatisticsYearSelect } from "@/features/statistics/hooks/UseStatisticsYearSelector";
import { ResultTable } from "@/features/statistics/components/ResultTable";
import { getNegotiationStatusCodes } from "@/features/statistics/lib/transformers/NegotiationStatusFormModelTransformer";
import { Box, Row, SeparatorLine } from "@stenajs-webui/core";
import { Card, CardBody, CardHeader } from "@stenajs-webui/elements";
import * as React from "react";
import { useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import { useRoutePairListQuery } from "@/features/statistics/hooks/UseRoutePairListQuery";
import { useRoutePairListVolumeAndTargetQuery } from "@/features/statistics/hooks/UseRoutePairListVolumeAndTargetQuery";
import { transformResultToTableRow } from "../lib/transformers/route-pair.transformer";
import { FppSearchFilter } from "@/features/search-filter/components/FppSearchFilter";
import { SearchFilterContext } from "@stenajs-webui/filter";
import { createFppSearchFilterActions } from "@/features/search-filter/redux/reducer";
import { useDispatch } from "react-redux";
import {
  SearchFilterSelectorContext,
  SearchFilterSelectorContextType,
} from "@/features/search-filter/context/searchFilterSelectorContext";
import { statisticsByRouteFilterSettings } from "../config/StatisticsByRouteFilterConfig";

export const StatisticsByRoutePairTable: React.FC = () => {
  const sticky = true;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { committedState, workspaceState } = useContext(
    SearchFilterSelectorContext
  ) as SearchFilterSelectorContextType;

  const { year: negotiationYear } = useStatisticsYearSelect();
  const { currency: currencyCode } = useCurrencySelection();
  const { routePairs } = committedState;

  const {
    routePairCustomerOrVolumeFilter,
    setRoutePairCustomerOrVolumeFilter,
    routePairSelectedStatuses,
    setRoutePairSelectedStatuses,
  } = useContext(StatisticsContext) as StatisticsContextType;

  const { loading, routePairItems, fetchRoutePairList } =
    useRoutePairListQuery();

  const negotiationStatusCodes = useMemo(
    () => getNegotiationStatusCodes(routePairSelectedStatuses),
    [routePairSelectedStatuses]
  );

  const { loading: loadingVolumeAndTarget, volumeAndTarget } =
    useRoutePairListVolumeAndTargetQuery({
      year: negotiationYear,
      routePairIds: routePairs.map((u) => u.id),
      negotiationStatusCodes,
      currencyCode,
    });

  const resultRows = routePairItems?.map((routePairItem) =>
    transformResultToTableRow(
      routePairItem,
      loadingVolumeAndTarget ? [] : volumeAndTarget ?? [],
      routePairCustomerOrVolumeFilter
    )
  );

  const resultConfig = createResultTableConfig(
    "Route",
    (id) => navigate(routeFactory.statistics.routeDetails(id)),
    currencyCode,
    routePairSelectedStatuses,
    setRoutePairSelectedStatuses,
    routePairCustomerOrVolumeFilter,
    setRoutePairCustomerOrVolumeFilter,
    sticky,
    true
  );

  const onSearch = () => {
    const { routePairs } = workspaceState.formModel;
    routePairs.length > 0 &&
      fetchRoutePairList({
        variables: {
          filterOptions: {
            routePairIds: routePairs.map((routePair) => routePair.id),
            year: negotiationYear,
          },
        },
      });
  };

  useEffect(() => {
    onSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [negotiationYear]);

  return (
    <Card minWidth={"1070px"}>
      <CardHeader text={"Route"} />
      <CardBody>
        <Row>
          <Box>
            <SearchFilterContext
              state={workspaceState}
              actions={createFppSearchFilterActions(
                "statistics.statisticsByRoutePair"
              )}
              dispatch={dispatch}
            >
              <FppSearchFilter
                settings={statisticsByRouteFilterSettings}
                onSearch={onSearch}
              />
            </SearchFilterContext>
          </Box>
        </Row>
      </CardBody>
      <SeparatorLine />
      <ResultTable
        items={resultRows}
        config={resultConfig}
        loading={loading || loadingVolumeAndTarget}
        noItemsLabel={"Please select routes to show statistics."}
      />
    </Card>
  );
};
